import { inject } from 'mobx-react';
import * as React from 'react';

import { PropertyNavigationBar } from '@shared-ui/lodging-property-details';
import { RefTarget } from '@shared-ui/ref-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { useRefProvider } from 'src/common/stores/contexts/ref-provider';

// UITK
import { UitkSticky } from 'uitk-react-sticky';
import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';

// Components
import { SharedUiWrapperProps, withSharedUiPropertyDetails } from './shared-ui-property-details';
import { NavBarWithQuickReserveButton } from '../../navbar-reserve-btn';
import * as viewportListener from 'components/common/lazy/viewport-listener';

interface NavbarWrapperProps extends SharedUiWrapperProps {
  showReserveBtn?: boolean;
  showPriceOnLoad?: boolean;
  showNightlyPrice?: boolean;
  showReserveBtnOnLoad?: boolean;
  currentHotel?: CurrentHotelStore;
}

const NavigationBarWrapper: React.FunctionComponent<NavbarWrapperProps> = inject('currentHotel')(
  ({ inputs, showReserveBtn, showPriceOnLoad = false, showNightlyPrice, showReserveBtnOnLoad, currentHotel }) => {
    const [priceboxInView, setPriceboxInView] = React.useState(!showPriceOnLoad);
    const [reserveBtnInView, setReserveBtnInView] = React.useState(showReserveBtnOnLoad);
    const [singleOfferRef, roomsAndRatesSectionRef] = useRefProvider('singleOfferPrice', 'roomsAndRatesSection');

    const singleOfferRefCurrent = singleOfferRef?.current;
    const roomsAndRatesSectionRefCurrent = roomsAndRatesSectionRef?.current;

    React.useEffect(() => {
      if (singleOfferRefCurrent) {
        viewportListener.subscribe('window', singleOfferRefCurrent, {
          didEnter: () => setPriceboxInView(true),
          didExit: () => setPriceboxInView(false),
        });
      }
    }, [singleOfferRefCurrent]);

    React.useEffect(() => {
      if (roomsAndRatesSectionRefCurrent) {
        new IntersectionObserver(entries => entries.forEach(entry => setReserveBtnInView(!entry.isIntersecting)), {
          rootMargin: '0% 0% -50% 0%',
        }).observe(roomsAndRatesSectionRefCurrent);
      }
    }, [roomsAndRatesSectionRefCurrent]);

    return (
      <>
        <UitkSticky zIndex={400}>
          <RefTarget name="navigation">
            <UitkCardAloha className="uitk-flat-border-top uitk-flat-border-bottom">
              <UitkCardAlohaContentSection border="bottom" padded={false}>
                {(showReserveBtn && !priceboxInView) ||
                (!showNightlyPrice && reserveBtnInView && !currentHotel.searchCriteria.isDateless) ? (
                  <NavBarWithQuickReserveButton inputs={inputs} showNightlyPrice={showNightlyPrice} />
                ) : (
                  <PropertyNavigationBar inputs={inputs} />
                )}
              </UitkCardAlohaContentSection>
            </UitkCardAloha>
          </RefTarget>
        </UitkSticky>
      </>
    );
  }
);

export const SharedUiNavigationBarWrapper = withSharedUiPropertyDetails(NavigationBarWrapper);
SharedUiNavigationBarWrapper.displayName = 'SharedUiNavigationBarWrapper';
